import { gql, useMutation, useQuery } from '@apollo/client'
import { ClickAwayListener } from '@mui/material'
import Hls from 'hls.js'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GetCurrentUserQualitySettingQuery } from '~/__generated__/graphql'
import { useLogger } from '../../useLogger'
import { useVideoScale } from '../../useVideoScale'
import { PlayerButton } from '../PlayerButton'
import { MenuItem } from './MenuItem'

type Props = {
  video: HTMLVideoElement
  hls: Hls
}

const GET_CURRENT_USER_QUALITY_SETTING_QUERY = gql`
  query getCurrentUserQualitySetting {
    currentUser {
      id
      qualitySetting
    }
  }
`

const UPDATE_CURRENT_USER_QUALITY_SETTING_MUTATION = gql`
  mutation updateCurrentUserQualitySetting($quality: Int) {
    updateUser(input: { qualitySetting: $quality }) {
      errors
    }
  }
`

export const Quality = ({ hls }: Props) => {
  const { data, refetch } = useQuery<GetCurrentUserQualitySettingQuery>(
    GET_CURRENT_USER_QUALITY_SETTING_QUERY,
  )
  const [updateQualitySetting] = useMutation(UPDATE_CURRENT_USER_QUALITY_SETTING_MUTATION)
  const { videoScale } = useVideoScale()
  const { t } = useTranslation()
  const [showMenu, setShowMenu] = useState(false)
  const [levelIndex, setLevelIndex] = useState(-1)
  const { clickQualityButton, changeQuality } = useLogger()

  useEffect(() => {
    const handleParsed = () => {
      if (data?.currentUser) {
        const newLevel = hls.levels.findIndex(
          (level) => level.height === data.currentUser?.qualitySetting,
        )
        setLevelIndex(newLevel)
        hls.currentLevel = newLevel
      }
    }

    hls.on(Hls.Events.MANIFEST_PARSED, handleParsed)

    return () => {
      hls.off(Hls.Events.MANIFEST_PARSED, handleParsed)
    }
  }, [data, hls])

  const handleChangeQuality = useCallback(
    (selectLevelIndex: number) => {
      hls.currentLevel = selectLevelIndex
      setLevelIndex(selectLevelIndex)
      changeQuality(hls.levels[selectLevelIndex]?.height.toString() || 'auto')
      updateQualitySetting({
        variables: { quality: hls.levels[selectLevelIndex]?.height || null },
      }).then(() => {
        refetch()
      })
    },
    [hls, changeQuality, updateQualitySetting, refetch],
  )

  return (
    <ClickAwayListener onClickAway={() => setShowMenu(false)}>
      <div style={{ position: 'relative' }}>
        <PlayerButton
          onClick={() => {
            setShowMenu(!showMenu)
            clickQualityButton()
          }}
        >
          <Image
            src='/player/quality.png'
            alt='Change Quality'
            width={videoScale * 52}
            height={videoScale * 52}
          />
          {videoScale > 0.4 && (
            <span
              style={{
                fontSize: `${videoScale * 24}px`,
                fontWeight: 'bold',
                marginLeft: '8px',
              }}
            >
              {t('Quality')}
            </span>
          )}
        </PlayerButton>
        {showMenu && (
          <div
            style={{
              minWidth: '120px',
              position: 'absolute',
              bottom: `${videoScale * 104}px`,
              left: 0,
              backgroundColor: '#EBECED',
              color: 'black',
              borderRadius: '8px',
              padding: '8px 0',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {hls.levels.map((level, i) => (
              <MenuItem
                key={level.height}
                onClick={() => handleChangeQuality(i)}
                active={levelIndex === i}
                text={`${level.height}p`}
              />
            ))}
            <MenuItem
              key='auto'
              onClick={() => handleChangeQuality(-1)}
              active={levelIndex === -1}
              text={t('Auto')}
            />
          </div>
        )}
      </div>
    </ClickAwayListener>
  )
}
